import React, { useEffect, useState } from "react";
import { getDashboardConfigs, saveDashboardConfig, deleteDashboardConfig, getEntities } from "../../API";

const CHART_GROUPS = [
  {
    label: "KPI Cards",
    options: [
      { key: "kpiTotal",        label: "Total Scans" },
      { key: "kpiStores",       label: "Stores Visited" },
      { key: "kpiDistribution", label: "Distribution %" },
      { key: "kpiStockLevel",   label: "Stock Level %" },
      { key: "kpiAvgPrice",     label: "Avg Price" },
      { key: "kpiMedianPrice",  label: "Median Price" },
    ],
  },
  {
    label: "Charts",
    options: [
      { key: "trend",    label: "Monthly Trend" },
      { key: "byBanner", label: "By Major Retailer" },
      { key: "byItem",   label: "By Item" },
      { key: "pricing",  label: "Pricing" },
    ],
  },
];

const CHART_OPTIONS = CHART_GROUPS.flatMap((g) => g.options);
const DEFAULT_CHARTS = CHART_OPTIONS.map((c) => c.key).join(",");

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function ConfigForm({ manufacturers, existing, onSave, onCancel }) {
  const [manufacturerId, setManufacturerId] = useState(String(existing?.manufacturerId ?? ""));
  const [visibleSet, setVisibleSet] = useState(
    new Set((existing?.visibleCharts ?? DEFAULT_CHARTS).split(","))
  );

  const toggle = (key) => {
    setVisibleSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mfr = manufacturers.find((m) => String(m.id) === manufacturerId);
    onSave({
      id: existing?.id,
      manufacturerId: parseInt(manufacturerId),
      manufacturerName: mfr?.name ?? "",
      visibleCharts: CHART_OPTIONS.map((c) => c.key).filter((k) => visibleSet.has(k)).join(","),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="border rounded p-3 mb-4 bg-light">
      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <label className="form-label fw-medium small">Manufacturer</label>
          <select
            className="form-select form-select-sm"
            value={manufacturerId}
            onChange={(e) => setManufacturerId(e.target.value)}
            required
            disabled={!!existing}
          >
            <option value="">Select…</option>
            {manufacturers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label fw-medium small d-block mb-2">Visible Charts</label>
          <div className="row g-3">
            {CHART_GROUPS.map((group) => (
              <div key={group.label} className="col-md-6">
                <div className="text-muted small fw-semibold mb-1">{group.label}</div>
                <div className="d-flex flex-wrap gap-3">
                  {group.options.map((c) => (
                    <div key={c.key} className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`chart-${c.key}`}
                        checked={visibleSet.has(c.key)}
                        onChange={() => toggle(c.key)}
                      />
                      <label className="form-check-label small" htmlFor={`chart-${c.key}`}>
                        {c.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="d-flex gap-2 mt-3">
        <button type="submit" className="btn btn-primary btn-sm">Save</button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function DashboardConfigs() {
  const [configs, setConfigs] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      getDashboardConfigs(),
      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
    ])
      .then(([cfgs, mfrs]) => {
        setConfigs(cfgs);
        setManufacturers(normalizeList(mfrs).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (config) => {
    try {
      const saved = await saveDashboardConfig(config);
      setConfigs((prev) => {
        const idx = prev.findIndex((c) => c.id === saved.id);
        return idx >= 0 ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved];
      });
      setShowForm(false);
      setEditingConfig(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this config?")) return;
    try {
      await deleteDashboardConfig(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const chartLabel = (key) => CHART_OPTIONS.find((c) => c.key === key)?.label ?? key;

  if (loading) {
    return (
      <div className="p-4">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  return (
    <div className="flex-grow-1 overflow-auto p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Dashboard Client Configs</h5>
        {!showForm && !editingConfig && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            Add Config
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <ConfigForm
          manufacturers={manufacturers}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingConfig && (
        <ConfigForm
          manufacturers={manufacturers}
          existing={editingConfig}
          onSave={handleSave}
          onCancel={() => setEditingConfig(null)}
        />
      )}

      {configs.length === 0 && !showForm ? (
        <p className="text-muted small">No configs yet. Click "Add Config" to create one.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
            <thead className="table-light">
              <tr>
                <th>Manufacturer</th>
                <th>Visible Charts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c.id}>
                  <td>{c.manufacturerName}</td>
                  <td className="text-muted small">
                    {(c.visibleCharts ?? DEFAULT_CHARTS).split(",").map(chartLabel).join(", ")}
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => { setEditingConfig(c); setShowForm(false); }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
